import {ReactElement} from 'react';

import * as PanelTypes from '../../state/views/panel/types';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {PartRefFromType} from '../../state/views/types';
import {RunHistoryKeyInfo, RunHistoryKeyType} from '../../types/run';
import * as Panels from '../../util/panels';
import * as PanelSettings from '../../util/panelsettings';
import {VegaPanelConfig} from '../PanelVega';
import {SectionMetadata} from './PanelBankSections';

export enum PanelBankConfigState {
  Init,
  Ready,
}

export enum OrganizationPrefix {
  FirstPrefix = 1,
  LastPrefix,
}

export enum SectionPanelSorting {
  None,
  Manual,
  Alphabetical,
}

export interface DefaultNonVegaPanelSpec {
  type: 'default-panel';
  metrics: string[];
  keyType: string;
  defaultSection: string;
  defaultXAxis?: string;
}

interface DefaultVegaPanelSpec {
  type: 'legacy-vega';
  keyType: string;
  config: VegaPanelConfig;
  defaultSection: string;
}

interface FoundNonVegaPanelSpec {
  type: 'default-panel';
  metrics: string[];
  currentRef: PartRefFromType<'panel'>;
  defaultXAxis?: string | null;
}

interface FoundVegaPanelSpec {
  type: 'legacy-vega';
  viz: VegaPanelConfig;
  currentRef: PartRefFromType<'panel'>;
}

export interface DefaultPanelSpecs {
  [key: string]: DefaultNonVegaPanelSpec | DefaultVegaPanelSpec;
}

export interface FoundPanelSpecs {
  [key: string]: FoundNonVegaPanelSpec | FoundVegaPanelSpec;
}

export interface AddPanel {
  type: 'add';
  spec: DefaultNonVegaPanelSpec | DefaultVegaPanelSpec | APIAddedPanelSpec;
}

interface UpdateNonVegaPanelSpec {
  type: 'default-panel';
  metrics: string[];
  defaultXAxis?: string;
}

interface UpdateVegaPanelSpec {
  type: 'legacy-vega';
  config: VegaPanelConfig;
}

export interface UpdatePanel {
  type: 'update';
  currentRef: PartRefFromType<'panel'>;
  spec: UpdateNonVegaPanelSpec | UpdateVegaPanelSpec | APIAddedPanelSpec;
}

export interface PanelBankDiff {
  [key: string]: AddPanel | UpdatePanel;
}

export interface APIAddedPanelBankDiff {
  [key: string]: {
    type: 'update';
    currentRef: PartRefFromType<'panel'>;
    spec: APIAddedPanelSpec;
  };
}

export interface FoundAPIAddedPanelSpecs {
  [key: string]: {
    config: Panels.PanelConfig;
    currentRef: PartRefFromType<'panel'>;
    viewType: string;
    type: 'api-added-panel';
  };
}

interface APIAddedPanelSpec {
  config: Panels.PanelConfig;
  viewType:
    | 'Media Browser'
    | 'Run History Line Plot'
    | 'Multi Run Table'
    | 'Markdown Panel'
    | 'Scalar Chart'
    | 'Vega'
    | 'Vega2'
    | 'Vega3'
    | 'Scatter Plot'
    | 'Parallel Coordinates Plot'
    | 'Run Comparer'
    | 'Code Comparer'
    | 'Bar Chart'
    | 'Parameter Importance';
  type: 'api-added-panel';
  keyType?: string;
  defaultSection: string;
}

export interface APIAddedPanelSpecs {
  [key: string]: APIAddedPanelSpec;
}

interface KeyQueryArgSpec {
  name: 'keys' | 'extraKeys' | 'foldKeys' | 'tableColumns';
  value: string[];
}

interface TableQueryArgSpec {
  name: 'tableKey';
  value: string;
}

export interface QueryArgSpecs
  extends Array<TableQueryArgSpec | KeyQueryArgSpec> {}

export interface PanelBankSettings {
  autoOrganizePrefix?: OrganizationPrefix;
  defaultMoveToSectionName?: string; // the name of the default section for the "Move panel to..." feature
  showEmptySections: boolean; // if false, we'll hide sections with zero panels
  sortAlphabetically: boolean; // sort panels alphabetically
  toggleRunAggregation: boolean; // flips "bucketing" of runs in the panel visualizations
}

export interface PanelBankConfig {
  state: PanelBankConfigState;
  sections: ReadonlyArray<PanelBankSectionConfig>;
  settings: PanelBankSettings;
}

export interface PanelBankSectionConfig {
  name: string;
  panels: ReadonlyArray<Panels.LayedOutPanel>;
  isOpen: boolean;
  flowConfig: PanelBankFlowSectionConfig;
  type: 'grid' | 'flow';
  sorted: SectionPanelSorting;
  localPanelSettings: PanelSettings.Settings;
}

export interface PanelBankFlowSectionConfig {
  snapToColumns: boolean;
  columnsPerPage: number;
  rowsPerPage: number;
  gutterWidth: number;
  boxWidth: number;
  boxHeight: number;
}

// These are shared by PanelBankFlowSection and PanelBankGridSection
export type PanelBankSectionComponentSharedProps = SectionMetadata & {
  readOnly?: boolean;
  panelBankWidth: number;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  addVisButton?: ReactElement;
  // HAXX: this is used to have the panel bank section rerender
  // when the panel settings change
  panelSettings?: PanelSettings.Settings;
  renderPanel(
    panelRef: PanelTypes.Ref,
    onContentHeightChange?: (h: number) => void
  ): JSX.Element;
  movePanelBetweenSections(
    panelRef: PanelTypes.Ref,
    fromSectionRef: PanelBankSectionConfigTypes.Ref,
    toSectionRef: PanelBankSectionConfigTypes.Ref,
    toIndex?: number,
    inactivePanelRefIDs?: Set<string>
  ): void;
};

export interface PanelIsActiveParams {
  section: PanelBankSectionConfig;
  panel: Panels.LayedOutPanel;
  historyKeyInfo: RunHistoryKeyInfo;
  keyTypes?: {[key: string]: RunHistoryKeyType};
  singleRun?: boolean;
  // Note that searchRegex is optional.
  // If it is undefined, panelIsActive will return true for search hits *and* search misses
  searchRegex?: RegExp | null;
}

export interface PanelBankSectionConfigWithVisiblePanels
  extends PanelBankSectionConfig {
  ref: PanelBankSectionConfigTypes.Ref;
  visiblePanels: ReadonlyArray<Panels.LayedOutPanel>;
}
